import "./ClinicalFormDetailsView.css";
import React, { ChangeEvent, useEffect, useState } from "react";
import { MemberAnswerInfo, QuestionInfo } from "clinical-forms-experience-client";
import infoIcon from "../../assets/img/info-filled-icon.svg";
import { ClinicalFormsConstants } from "./ClinicalFormsConstants";

interface Props {
  index: number;
  parentIndex?: number;
  question: QuestionInfo;
  savedAnswers?: Map<string, MemberAnswerInfo>;
  onAnswerChange: (questionId: string, answer: MemberAnswerInfo) => void;
  readOnly: boolean;

  onMount?: (id: string) => void;
  onUnmount?: (id: string) => void;
  hiddenQuestionIds?: Set<string>;
  assessmentId?: string;
}

const ClinicalFormQuestionTableView: React.FC<Props> = ({
  index,
  parentIndex = -1,
  question,
  savedAnswers,
  onAnswerChange,
  readOnly,
  onMount,
  onUnmount,
  hiddenQuestionIds,
  assessmentId
}: Props) => {
  const [optionsSelected, setOptionsSelected] = React.useState<Set<string>>(
    new Set()
  );
  const [firstDependentQuestion , setFirstDependentQuestion] = useState<string | null>(null);
  const [lastDependentQuestion , setLastDependentQuestion] = useState<string | null>(null);

  const [notes, setNotes] = React.useState<string>();

  useEffect(() => onMount?.(question.id), [question]);
  useEffect(() => () => onUnmount?.(question.id), []);

  useEffect(() => {
    if (hiddenQuestionIds && hiddenQuestionIds?.size > 0) {
      const hiddenIdsArray = Array.from(hiddenQuestionIds);
      setFirstDependentQuestion(hiddenIdsArray[0]);
      setLastDependentQuestion(hiddenIdsArray[hiddenIdsArray.length - 1]);
    }
  }, [hiddenQuestionIds]);

  useEffect(() => {
    const savedAnswer = savedAnswers?.get(question.id);
    if (!savedAnswer) {
      return;
    }
    setOptionsSelected(new Set(parseOptions(savedAnswer?.answer, question)));
    setNotes(savedAnswer?.notes);
  }, [savedAnswers]);

  const onChoicesAnswersChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (question.type === "single-choice") {
        optionsSelected.clear();
      }
      optionsSelected.add(event.target.value);
    } else {
      optionsSelected.delete(event.target.value);
    }

    setOptionsSelected(new Set(optionsSelected));
    const answersString = Array.from(optionsSelected).join(",");
    onAnswerChange(question.id, { questionId: question.id, answer: answersString, notes: notes });
  };

  // radio button deselection
  const onChoiceClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (
      question.type === "single-choice" &&
      !question.isRequired &&
      optionsSelected.has(event.currentTarget.value)
    ) {
      optionsSelected.delete(event.currentTarget.value);
      setOptionsSelected(new Set(optionsSelected));
      onAnswerChange(question.id, { questionId: question.id, answer: "", notes: notes });
    }
  };

  return (
    <>
      {/* Banner BEFORE question 3 */}
      {question.id === firstDependentQuestion && assessmentId === ClinicalFormsConstants.PsychosocialFormId && (
        <tr>
          <td colSpan={question.options?.length ? question.options.length + 1 : 2} className="m-0 p-0">
            <div className="assessment-info-banner p-4" style={{ position: 'static' }}>
              <div className="d-flex align-items-center">
                <div className="flex-0 icon-circle">
                  <img src={infoIcon} alt="info-icon"/>
                </div>
                <div className="flex ms-2">
                  <strong>Interpretation:</strong> A score of <strong>3 points</strong> is the preferred cut-off for identifying possible cases in which further diagnostic evaluation for generalized anxiety disorder is warranted (<strong>GAD-7</strong>).
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      <tr>
        <td className="border">
          <div className={`pe-2 ${parentIndex >= 0 ? "ps-6" : ""}`}>
            <span className="float-start text-end me-1" style={{ width: '25px' }}>{parentIndex >= 0 && <span>{parentIndex + 1}.</span>}{index + 1}.</span>
            <span className="float-start" style={{ width: 'calc(100% - 30px)' }}>
              {question.questionText}
              {question.isRequired && (
                <>
                  <span
                    className="cn-required-asterisk"
                    title="Required"
                    aria-label="Required"
                  >
                    *
                  </span>
                  <span
                    className="cn-required-badge"
                    title="Required"
                    aria-label="Required"
                  >
                    Required
                  </span>
                </>
              )}
            </span>
          </div>
        </td>
        {question.options?.map((option) => (
          <td key={option.id} className="text-titlecase text-center align-middle">
            <label className="p-2">
            <input
              className="form-check-input"
              type={
                question.type === "single-choice" ? "radio" : "checkbox"
              }
              name={`question-${question.id}`}
              value={option.id}
              onChange={onChoicesAnswersChange}
              onClick={onChoiceClick}
              checked={optionsSelected.has(option.id)}
              disabled={readOnly}
              style={{ border: '1px #004c4c solid' }}
            />
            </label>
          </td>
        ))}
      </tr>
      {/* Banner AFTER question 7 */}
      {question.id === lastDependentQuestion && assessmentId === ClinicalFormsConstants.PsychosocialFormId && (
        <tr>
          <td colSpan={question.options?.length ? question.options.length + 1 : 2} className="m-0 p-0">
            <div className="assessment-info-banner p-4" style={{ position: 'static' }}>
              <div className="d-flex align-items-top">
                <div className="flex-0 icon-circle">
                  <img src={infoIcon} alt="info-icon" />
                </div>
                <div className="flex ms-2">
                  <strong>Interpretation:</strong> When screening for anxiety disorders, a score of 8 or greater represents a reasonable cut-point for identifying probable cases of generalized anxiety disorder; further diagnostic assessment is warranted to determine the presence and type of anxiety disorder.
                  <br />
                  The following cut-offs correlate with level of anxiety severity:
                  <ul className="mb-0">
                    <li><strong>Score 0-4:</strong> Minimal Anxiety</li>
                    <li><strong>Score 5-9:</strong> Mild Anxiety</li>
                    <li><strong>Score 10-14:</strong> Moderate Anxiety</li>
                    <li><strong>Score greater than 15:</strong> Severe Anxiety</li>
                  </ul>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {(question.type === "single-choice" || question.type === "multiple-choice") &&
      question.options?.map(
        (option) =>
          optionsSelected.has(option.id) &&
          option.dependentQuestions?.map((dependentQuestion, childIndex) => (
            <ClinicalFormQuestionTableView
              key={dependentQuestion.id}
              index={childIndex}
              parentIndex={index}
              question={dependentQuestion}
              savedAnswers={savedAnswers}
              onAnswerChange={onAnswerChange}
              readOnly={readOnly}
              onMount={onMount}
              onUnmount={onUnmount}
            />
          ))
      )}
    </>
  );
};

export default ClinicalFormQuestionTableView;

function parseOptions(
  savedAnswer: string | undefined,
  question: QuestionInfo
): string[] {
  if (!savedAnswer || savedAnswer === "") {
    return [];
  }
  return question.type === "text" ? [savedAnswer] : savedAnswer.split(",");
}
